import {
  LocaleProvider,
  CofsClientProvider,
  BXPExpressClientProvider,
  CnasClientProvider,
  HamletClientProvider,
  IdentityClientProvider,
  StargateClientProvider,
} from "@atlassiansox/bxpkit-core";

import Environment from "../common/environment";
import {
  CofsClient,
  PUBLIC_PROD_BASE_URL as PROD_BASE_URL_COFS,
  STAGING_BASE_URL as STAGING_BASE_URL_COFS,
} from "@atlassiansox/asc-cofs";
import { StargateClient } from "@atlassiansox/asc-stargate";
import { IdentityClient } from "@atlassiansox/asc-identity";
import {
  BXPExpressClient,
  DEFAULT_BASE_URL as STAGING_BASE_URL_BXP_EXPRESS,
} from "@atlassiansox/bxp-express-client";
import { FeatureFlagsProvider } from "@atlassiansox/bxp-react-feature-flags";
import {
  CnasClient,
  PRODUCTION_BASE_URL as PRODUCTION_BASE_URL_CNAS,
  STAGING_BASE_URL as STAGING_BASE_URL_CNAS,
} from "@atlassiansox/asc-cnas";
import {
  HamletClient,
  DEFAULT_BASE_URL as DEFAULT_BASE_URL_HAMS,
} from "@atlassiansox/asc-hamlet";
import React from "react";
import ReactDOM from "react-dom";
import { addLocaleData, IntlProvider } from "react-intl";

import locale_en from "react-intl/locale-data/en";
import locale_ja from "react-intl/locale-data/ja";
import locale_fr from "react-intl/locale-data/fr";
import locale_de from "react-intl/locale-data/de";
import locale_es from "react-intl/locale-data/es";
import locale_br from "react-intl/locale-data/br";
import locale_zh from "react-intl/locale-data/zh";
import locale_ko from "react-intl/locale-data/ko";
import locale_ru from "react-intl/locale-data/ru";
import locale_pl from "react-intl/locale-data/pl";
import locale_it from "react-intl/locale-data/it";
import locale_nl from "react-intl/locale-data/nl";
import locale_fi from "react-intl/locale-data/fi";
import locale_hu from "react-intl/locale-data/hu";
import locale_ro from "react-intl/locale-data/ro";
import locale_cs from "react-intl/locale-data/cs";

import * as componentList from "./component-list";
import { Suspense } from "react";
import WacAppSwitcher from "./app-switcher/app";
addLocaleData([
  ...locale_en,
  ...locale_ja,
  ...locale_fr,
  ...locale_de,
  ...locale_es,
  ...locale_br,
  ...locale_zh,
  ...locale_ko,
  ...locale_ru,
  ...locale_pl,
  ...locale_it,
  ...locale_nl,
  ...locale_fi,
  ...locale_hu,
  ...locale_ro,
  ...locale_cs,
]);

// set Available Components
const availableComponents = {
  ...componentList,
  WacAppSwitcher,
};

class App {
  init() {
    this.findComponents();
  }

  findComponents() {
    let renderedComponentTypes = [];
    const componentTags = document.querySelectorAll(
      'script[type="text/jsx-component"]'
    );

    componentTags.forEach((element) => {
      const params = this.parseComponentProps(element);

      if (params) {
        if (params.renderOnce && !!renderedComponentTypes[params.type]) {
          return;
        }
        renderedComponentTypes[params.type] = true;

        const defaultTrackProperties = {
          eventType: "Track",
          eventContainer: null,
          eventColumn: null,
          magnoliaComponentId: params.domRootId || null,
          eventComponent: params.type || null,
          event: "clicked",
          inputValue: null,
          parentLabel: null,
          label: null,
          productKey: null,
          productVersion: null,
          productEdition: null,
          productPlatform: null,
          schemaVersion: 1,
        };
        this.renderComponent({ defaultTrackProperties, ...params });
      }
    });
  }

  parseComponentProps(element) {
    const componentData = element.innerHTML
      .replace("/* <sl:translate_json> */", "")
      .replace("/* </sl:translate_json> */", "");

    try {
      return JSON.parse(componentData);
    } catch (error) {
      console.log(error);
      return false;
    }
  }
  getClients() {
    const isDev = Environment.isDev();
    const isStaging = Environment.isStaging();
    const cnasBaseUrl = isStaging
      ? STAGING_BASE_URL_CNAS
      : PRODUCTION_BASE_URL_CNAS;
    const bxpExpressBaseUrl = isStaging
      ? STAGING_BASE_URL_BXP_EXPRESS
      : `${Environment.getWacIntegrationUrl()}/endpoint`;
    const cofsBaseUrl =
      isDev || isStaging ? STAGING_BASE_URL_COFS : PROD_BASE_URL_COFS;
    const hamsBaseUrl = isStaging
      ? `${Environment.getWacIntegrationUrl()}/apis/stg/hamlet`
      : DEFAULT_BASE_URL_HAMS;
    const cofsClient = new CofsClient({ baseUrl: cofsBaseUrl });
    const cnasClient = new CnasClient({ baseUrl: cnasBaseUrl });
    const bxpExpressClient = new BXPExpressClient({
      baseUrl: bxpExpressBaseUrl,
    });
    const hamletClient = new HamletClient({ baseUrl: hamsBaseUrl });
    const identityClient = new IdentityClient({
      baseUrlStargate: Environment.getAPIPrivateURL(),
      baseUrlIdentity: Environment.getIdentityBaseUrl(),
    });
    const stargateClient = new StargateClient({
      baseUrl: Environment.getAPIPrivateURL(),
    });
    return {
      bxpExpressClient,
      cofsClient,
      cnasClient,
      hamletClient,
      identityClient,
      stargateClient,
    };
  }

  renderComponent({ type, props, domRootId, defaultTrackProperties }) {
    const TagName = availableComponents[type] || null;
    if (!TagName) return;
    const propsWithDefaultTracking = { defaultTrackProperties, ...props };
    const {
      bxpExpressClient,
      cofsClient,
      cnasClient,
      hamletClient,
      identityClient,
      stargateClient,
    } = this.getClients();

    ReactDOM.render(
      <Suspense fallback={<div></div>}>
        <FeatureFlagsProvider featureFlags={window.featureFlags}>
          <LocaleProvider locale={document.documentElement.lang}>
            <IntlProvider
              locale={document.documentElement.lang}
              messages={props.labels}
            >
              <CofsClientProvider client={cofsClient}>
                <CnasClientProvider client={cnasClient}>
                  <BXPExpressClientProvider client={bxpExpressClient}>
                    <HamletClientProvider client={hamletClient}>
                      <IdentityClientProvider client={identityClient}>
                        <StargateClientProvider client={stargateClient}>
                          <TagName {...propsWithDefaultTracking} />
                        </StargateClientProvider>
                      </IdentityClientProvider>
                    </HamletClientProvider>
                  </BXPExpressClientProvider>
                </CnasClientProvider>
              </CofsClientProvider>
            </IntlProvider>
          </LocaleProvider>
        </FeatureFlagsProvider>
      </Suspense>,
      document.getElementById(domRootId)
    );
  }
}

const app = new App();
app.init();
